import React, { useState, useEffect } from 'react';
import { getBrands, getCategories, createBrand, updateBrand, deleteBrand, uploadImage } from '../../lib/api';
import { slugify } from '../../lib/utils';
import { toast } from 'sonner';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Checkbox } from '../ui/checkbox';
import { Plus, Edit, Trash2, Upload, Home, Check, Image } from 'lucide-react';

export default function AdminBrands() {
  const [brands, setBrands] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingBrand, setEditingBrand] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadingLight, setUploadingLight] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    description: '',
    category_id: '',
    logo: '',
    logo_light: '',
    show_on_home: false,
  });

  const fetchData = async () => {
    try {
      const [brandsRes, catsRes] = await Promise.all([
        getBrands(),
        getCategories(),
      ]);
      setBrands(brandsRes.data);
      setCategories(catsRes.data);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const homeBrandsCount = brands.filter(b => b.show_on_home).length;

  const handleNameChange = (e) => {
    const name = e.target.value;
    setFormData({ ...formData, name, slug: slugify(name) });
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploading(true);
    try {
      const res = await uploadImage(file, 'brand');
      setFormData({ ...formData, logo: res.data.url });
      toast.success('Logo uploaded');
    } catch (error) {
      toast.error('Failed to upload logo');
    } finally {
      setUploading(false);
    }
  };

  const handleLightLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploadingLight(true);
    try {
      const res = await uploadImage(file, 'brand');
      setFormData({ ...formData, logo_light: res.data.url });
      toast.success('Light logo uploaded');
    } catch (error) {
      toast.error('Failed to upload logo');
    } finally {
      setUploadingLight(false);
    }
  };

  const handleHomeToggle = async (brand) => {
    const newValue = !brand.show_on_home;
    try {
      await updateBrand(brand.id, { show_on_home: newValue });
      toast.success(newValue ? 'Added to homepage carousel' : 'Removed from homepage carousel');
      fetchData();
    } catch (error) {
      toast.error('Failed to update brand');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = {
        ...formData,
        category_id: formData.category_id || null,
      };
      
      if (editingBrand) {
        await updateBrand(editingBrand.id, submitData);
        toast.success('Brand updated successfully');
      } else {
        await createBrand(submitData);
        toast.success('Brand created successfully');
      }
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error('Failed to save brand');
    }
  };

  const handleEdit = (brand) => {
    setEditingBrand(brand);
    setFormData({
      name: brand.name,
      slug: brand.slug,
      description: brand.description || '',
      category_id: brand.category_id || '',
      logo: brand.logo || '',
      logo_light: brand.logo_light || '',
      show_on_home: brand.show_on_home || false,
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this brand?')) return;
    try {
      await deleteBrand(id);
      toast.success('Brand deleted successfully');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete brand');
    }
  };

  const resetForm = () => {
    setEditingBrand(null);
    setFormData({
      name: '',
      slug: '',
      description: '',
      category_id: '',
      logo: '',
      logo_light: '',
      show_on_home: false,
    });
  };

  const getCategoryName = (categoryId) => {
    if (!categoryId) return '-';
    return categories.find(c => c.id === categoryId)?.name || '-';
  };

  return (
    <div className="space-y-6" data-testid="admin-brands">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-serif text-[#0B1F3B]">Brands</h1>
          <p className="text-gray-500">
            Manage product brands ({brands.length} total) • 
            <span className="text-[#D4AF37] ml-1">
              {homeBrandsCount} on homepage carousel
            </span>
          </p>
        </div>
        <Button onClick={() => { resetForm(); setDialogOpen(true); }} className="btn-primary" data-testid="add-brand-btn">
          <Plus className="h-4 w-4 mr-2" />
          Add Brand
        </Button>
      </div>

      <div className="bg-white border border-gray-100 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-500">Loading...</div>
        ) : brands.length > 0 ? (
          <table className="w-full data-table">
            <thead>
              <tr>
                <th className="px-6 py-4 text-left">Logo</th>
                <th className="px-6 py-4 text-left">Name</th>
                <th className="px-6 py-4 text-left">Category</th>
                <th className="px-6 py-4 text-center">
                  <div className="flex items-center justify-center gap-1">
                    <Home className="h-4 w-4" />
                    Homepage
                  </div>
                </th>
                <th className="px-6 py-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {brands.map((brand) => (
                <tr key={brand.id}>
                  <td className="px-6 py-4">
                    {brand.logo ? (
                      <img src={brand.logo} alt={brand.name} className="w-16 h-10 object-contain" />
                    ) : (
                      <div className="w-16 h-10 bg-gray-100 rounded flex items-center justify-center">
                        <Image className="h-4 w-4 text-gray-400" />
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 font-medium">{brand.name}</td>
                  <td className="px-6 py-4 text-gray-500 text-sm">
                    {getCategoryName(brand.category_id)}
                  </td>
                  <td className="px-6 py-4 text-center">
                    <button
                      onClick={() => handleHomeToggle(brand)}
                      className={`inline-flex items-center justify-center w-8 h-8 rounded-md border transition-colors ${
                        brand.show_on_home 
                          ? 'bg-[#D4AF37] border-[#D4AF37] text-white' 
                          : 'border-gray-300 hover:border-[#D4AF37]'
                      } ${!brand.logo ? 'opacity-50 cursor-not-allowed' : ''}`}
                      disabled={!brand.logo}
                      title={brand.logo ? (brand.show_on_home ? 'Remove from carousel' : 'Add to carousel') : 'Upload logo first'}
                      data-testid={`home-toggle-${brand.id}`}
                    >
                      {brand.show_on_home && <Check className="h-4 w-4" />}
                    </button>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <button onClick={() => handleEdit(brand)} className="p-2 text-gray-400 hover:text-[#0B1F3B]">
                      <Edit className="h-4 w-4" />
                    </button>
                    <button onClick={() => handleDelete(brand.id)} className="p-2 text-gray-400 hover:text-red-500">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="p-12 text-center text-gray-500">
            <p>No brands yet. Click "Add Brand" to create one.</p>
          </div>
        )}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[550px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingBrand ? 'Edit Brand' : 'Add New Brand'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Name *</Label>
              <Input 
                id="name" 
                value={formData.name} 
                onChange={handleNameChange} 
                required 
                data-testid="brand-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="slug">Slug</Label>
              <Input 
                id="slug" 
                value={formData.slug} 
                onChange={(e) => setFormData({ ...formData, slug: e.target.value })} 
                data-testid="brand-slug-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Category (Optional)</Label>
              <Select 
                value={formData.category_id} 
                onValueChange={(val) => setFormData({ ...formData, category_id: val === 'none' ? '' : val })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No specific category</SelectItem>
                  {categories.map((cat) => (
                    <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Brand Logo Upload */}
            <div className="space-y-2">
              <Label>Brand Logo (for dark backgrounds)</Label>
              <div className="flex gap-2 items-center">
                <Input
                  value={formData.logo}
                  onChange={(e) => setFormData({ ...formData, logo: e.target.value })}
                  placeholder="Logo URL or upload"
                  className="flex-1"
                />
                <label className="cursor-pointer">
                  <input
                    type="file"
                    accept="image/*,.svg"
                    onChange={handleLogoUpload}
                    className="hidden"
                  />
                  <Button type="button" variant="outline" disabled={uploading} asChild>
                    <span>
                      <Upload className="h-4 w-4 mr-1" />
                      {uploading ? '...' : 'Upload'}
                    </span>
                  </Button>
                </label>
              </div>
              {formData.logo && (
                <div className="p-3 bg-[#0B1F3B] rounded mt-2 inline-block">
                  <img src={formData.logo} alt="Logo preview" className="h-12 w-auto object-contain" />
                </div>
              )}
              <p className="text-xs text-gray-400">SVG or PNG preferred. This logo displays on dark backgrounds.</p>
            </div>

            {/* Light Logo Variant (Optional) */}
            <div className="space-y-2">
              <Label>Light Logo Variant (Optional)</Label>
              <div className="flex gap-2 items-center">
                <Input
                  value={formData.logo_light}
                  onChange={(e) => setFormData({ ...formData, logo_light: e.target.value })}
                  placeholder="Light logo URL or upload"
                  className="flex-1"
                />
                <label className="cursor-pointer">
                  <input
                    type="file"
                    accept="image/*,.svg"
                    onChange={handleLightLogoUpload}
                    className="hidden"
                  />
                  <Button type="button" variant="outline" disabled={uploadingLight} asChild>
                    <span>
                      <Upload className="h-4 w-4 mr-1" />
                      {uploadingLight ? '...' : 'Upload'}
                    </span>
                  </Button>
                </label>
              </div>
              {formData.logo_light && (
                <div className="p-3 bg-gray-100 rounded mt-2 inline-block">
                  <img src={formData.logo_light} alt="Light logo preview" className="h-12 w-auto object-contain" />
                </div>
              )}
              <p className="text-xs text-gray-400">Dark version of logo for light backgrounds (optional).</p>
            </div>

            {/* Show on Home Toggle */}
            <div className="flex items-center gap-3 py-2 border-t pt-4">
              <Checkbox
                id="show_on_home"
                checked={formData.show_on_home}
                onCheckedChange={(checked) => setFormData({ ...formData, show_on_home: checked })}
                disabled={!formData.logo}
                data-testid="show-on-home-checkbox"
              />
              <Label htmlFor="show_on_home" className={`cursor-pointer flex items-center gap-2 ${!formData.logo ? 'opacity-50' : ''}`}>
                <Home className="h-4 w-4 text-[#D4AF37]" />
                Show on Homepage Carousel
              </Label>
            </div>
            {!formData.logo && (
              <p className="text-xs text-amber-600">Upload a logo to enable homepage carousel display.</p>
            )}

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea 
                id="description" 
                value={formData.description} 
                onChange={(e) => setFormData({ ...formData, description: e.target.value })} 
                rows={3} 
                placeholder="Brief brand description..."
              />
            </div>

            <div className="flex gap-4 pt-4">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="flex-1">Cancel</Button>
              <Button type="submit" className="btn-primary flex-1" data-testid="save-brand-btn">
                {editingBrand ? 'Update' : 'Create'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
